import { currentInstallationPack, hasPaidAccess, useBillingStore } from "./billing";
import { useAuthStore } from "./auth/store";
import { servedFreeBuildVariant } from "./equipment-cap-experiment";
import { canEnrollEquipmentCapExperiment } from "./equipment-cap-eligibility";
import { freeEquipmentLimit } from "./equipment-limit";

export type EquipmentLimitRuntime = {
  enforced: boolean;
  monetizationEnabled: boolean;
  entitled: boolean;
  /** Free counted equipment for the served variant: 10 (control), 5 (`cap5`), 6 (`cap6`)
   * or Infinity (`unlimited`). Meaningful only while `enforced`. */
  limit: number;
};

type EquipmentMonetizationState = {
  configReady: boolean;
  configEnabled: boolean;
  configUnavailable: boolean;
};

export function equipmentMonetizationEnabled(
  state: EquipmentMonetizationState,
  production = import.meta.env.PROD,
): boolean {
  return state.configReady
    ? state.configEnabled || state.configUnavailable
    : production;
}

/** Billing-disabled builds stay unmetered. A served experiment assignment
 * changes only the equipment policy, never the independent paid-access gate. */
export function equipmentLimitRuntime(): EquipmentLimitRuntime {
  const billing = useBillingStore.getState();
  const auth = useAuthStore.getState();
  // A missing billing response is not a confirmed disabled monetization flag.
  const monetizationEnabled = equipmentMonetizationEnabled({
    configReady: billing.configReady,
    configEnabled: billing.config.enabled,
    configUnavailable: billing.configUnavailable,
  });
  // A purchased pack includes future revisions of THIS installation even
  // after enrollment stops or the owner opts out of analytics.
  const entitled = hasPaidAccess(auth.currentProjectId) || currentInstallationPack() !== null;
  // Only an eligible visitor's served assignment changes the free count; a
  // stale or ineligible assignment falls back to the control policy.
  const variant = canEnrollEquipmentCapExperiment(auth, billing) ? servedFreeBuildVariant() : null;
  const limit = freeEquipmentLimit(variant ?? "control");
  return {
    monetizationEnabled,
    entitled,
    limit,
    enforced: monetizationEnabled && !entitled && Number.isFinite(limit),
  };
}

/** The free equipment count the store and paywalls enforce right now. */
export function effectiveEquipmentLimit(): number {
  return equipmentLimitRuntime().limit;
}
